/**
 * Allerte sui contenuti scaduti, e su che cosa manchi per rimediare.
 *
 * Perche' non e' un endpoint
 * --------------------------
 * Le allerte si calcolano qui, nel browser, come gia' lo scadenzario. La
 * ragione non e' architetturale ma di onesta': dipendono dalla **periodicita'
 * degli obblighi**, che non e' un dato di questo sistema — non e' nel seed
 * ANAC, non e' nel catalogo, non e' sulla tabella dei nodi, e le fonti ANAC
 * danno «attualita' del dato» come criterio senza fissare termini. L'unica
 * tabella esistente e' `CADENZE` in `scadenze.ts`, indicizzata sulle sezioni di
 * primo livello.
 *
 * Un elenco servito dall'API sarebbe percio' una verita' di servizio costruita
 * su un'ipotesi, e chi la legge non potrebbe piu' ispezionarla. Qui invece ogni
 * riga puo' dichiarare da dove viene il proprio numero — ed e' quello che fa.
 *
 * Che cosa il sistema **non** puo' sapere
 * ---------------------------------------
 * Se un sostituto sia dovuto. Non esiste alcuna relazione fra pubblicazioni che
 * dica «questa sostituisce quella», e `anno_riferimento` e' documentato come
 * filtro di presentazione del portale, non come periodo di validita'. Qui lo si
 * usa come **proxy** del periodo: e' il meccanismo che l'API stessa adotta
 * quando riapre la pubblicazione dello stesso anno, ma resta un proxy. Se un
 * giorno il modello dati avra' un periodo di validita' vero, la funzione da
 * riscrivere e' {@link successorePerAnnualita}.
 *
 * La risposta la da' l'operatore, e la conserva il giornale delle valutazioni.
 * Ogni verdetto registrato e' un pezzo del dato mancante.
 *
 * Il falso allarme di massa
 * -------------------------
 * Questo modulo ha un precedente da non ripetere, scritto in `scadenze.ts`: gli
 * obblighi mai valorizzati, iscritti fra gli adempimenti «a zero giorni»,
 * facevano leggere «121 in scadenza» al cruscotto di un ente appena avviato. Il
 * presidio piu' forte e' nel tipo — `giorni_residui: number | null`, e dove non
 * c'e' un termine non c'e' un numero — seguito da {@link eEnteInAvvio}, che
 * riconosce un ente che sta ancora popolando la sezione e in cui un elenco di
 * obblighi vuoti e' un piano di lavoro, non un allarme.
 */

import { TIPI_ALLERTA } from "../api/types";
import type {
  DataIt,
  IdNodo,
  NaturaObbligo,
  Nodo,
  Pubblicazione,
  RegolaObbligo,
  StatoPubblicazione,
  TipoAllerta,
  Valutazione,
} from "../api/types";
import { giorniDaOggi, oggi as oggiPredefinito } from "./formats";
import type { Cadenza, FonteCadenza } from "./scadenze";
import {
  cadenzaConFonte,
  dataArchiviazionePrevista,
  DURATA_PREDEFINITA_ANNI,
  fineDelDocumento,
  giornoPrima,
  giorniDiCadenza,
  indicePerIdNodo,
  nodiFoglia,
  scadenzaDelFile,
} from "./scadenze";

// --------------------------------------------------------------------------- //
// Costanti dichiarate, e dichiaratamente prudenziali
// --------------------------------------------------------------------------- //

/**
 * Giorni di tolleranza prima che un'annualita' non subentrata diventi allerta.
 *
 * **Non e' un termine di legge**: le fonti ANAC danno «attualita' del dato»
 * senza fissare una data entro cui il contenuto dell'anno nuovo debba esserci.
 * Novanta giorni sono una scelta prudenziale — il primo trimestre — e
 * l'interfaccia lo dichiara invece di far sembrare il numero una norma.
 */
export const GIORNI_TOLLERANZA_SUCCESSIONE = 90;

/**
 * Fin dove, nel futuro, si calcolano le allerte di fine pubblicazione: un
 * anno, come la scelta «12 mesi» di «In arrivo».
 *
 * «Scadenze e allerte» le tiene fuori da «Da gestire» finche' il termine non e'
 * passato ({@link allerteDaGestire}): le stesse date le mostra gia' «In
 * arrivo», da `calcolaScadenze`. Conta quindi per chi chiama
 * {@link calcolaAllerte} direttamente.
 */
export const GIORNI_ORIZZONTE_QUINQUENNIO = 365;

/**
 * Quota di obblighi scoperti oltre la quale l'ente si considera in avviamento.
 *
 * Sopra questa soglia la pagina smette di parlare di allerte e parla di un
 * piano di lavoro: su una sezione appena creata i 121 obblighi vuoti non sono
 * 121 inadempienze, sono il lavoro da fare.
 */
export const QUOTA_AVVIO = 0.5;

// --------------------------------------------------------------------------- //
// Tipi
// --------------------------------------------------------------------------- //

/** Gravita' di una riga, per il tono del chip e l'ordinamento. */
export type GravitaAllerta = "critico" | "attenzione" | "informativo";

/**
 * Le annualita' presenti su un obbligo, bozze comprese.
 *
 * E' la singola informazione che risponde da sola alla domanda «c'e' un
 * sostituto?»: vedere `2021 · 2022 · 2023 · 2025` fa saltare all'occhio il 2024
 * mancante senza che nessuno debba calcolarlo.
 */
export interface AnnualitaPresente {
  anno: number;
  stato: StatoPubblicazione;
  id: number;
  titolo: string;
}

export interface EvidenzaAllerta {
  annualita: AnnualitaPresente[];
  /** Bozza per l'anno successivo: non e' un sostituto, ma cambia il giudizio. */
  bozza_successiva?: AnnualitaPresente;
  documenti: number;
  data_ultimo_aggiornamento?: string;
  /**
   * Vero se la data di aggiornamento viene dai discendenti e non dal nodo.
   *
   * L'API calcola `data_ultimo_aggiornamento` come massimo fra il nodo **e i
   * suoi discendenti**, propagandolo agli antenati: su una sezione dice che
   * qualcosa sotto si e' mosso, non che quel nodo sia stato aggiornato. Dove
   * accade, la riga lo dichiara invece di spacciare il dato per proprio.
   */
  aggiornamento_risalito: boolean;
  /** Termine del quinquennio, dove la riga lo riguarda. */
  prima_pubblicazione?: DataIt;
}

export interface Allerta {
  /** Identita' stabile della riga: e' cio' che finisce nel giornale. */
  chiave: string;
  tipo: TipoAllerta;
  id_nodo: IdNodo;
  denominazione_nodo: string;
  chiave_periodo: string;
  pubblicazione_id?: number;
  cadenza: Cadenza;
  fonte_cadenza: FonteCadenza;
  /** Nodo da cui la cadenza e' ereditata, quando lo e'. */
  id_nodo_cadenza?: IdNodo;
  natura?: NaturaObbligo;
  termine?: DataIt;
  /**
   * I documenti che l'allerta riguarda, sulle allerte di fine pubblicazione e
   * sui file da sostituire: ogni documento ha la propria data, e quelli che
   * finiscono lo stesso giorno nella stessa pubblicazione stanno in una riga
   * sola. Assente su una pubblicazione senza documenti, che esce tutta intera.
   *
   * `in_correzione`: il documento online ha un sostituto in bozza. Scade lo
   * stesso — online c'e' ancora il file vecchio —, ma non si archivia finche' la
   * correzione non e' pubblicata o scartata (l'API lo rifiuterebbe), e non si
   * sostituisce una seconda volta: resta da pubblicare il sostituto.
   */
  documenti?: { id: number; titolo: string; in_correzione?: boolean }[];
  /**
   * La nota pubblica sull'assenza di contenuti, sulle righe degli obblighi
   * scoperti: e' cio' che il cittadino legge sul portale al posto del file.
   */
  nota_assenza?: { testo: string; il?: DataIt };
  /**
   * Giorni al termine, negativi se passato. **`null` quando un termine non
   * esiste**, e non zero: uno zero significherebbe «scade oggi» ed e'
   * esattamente il guasto che ha fatto leggere «121 in scadenza».
   */
  giorni_residui: number | null;
  gravita: GravitaAllerta;
  motivo: string;
  evidenza: EvidenzaAllerta;
  /** Verdetto che la sta spegnendo, se ce n'e' uno attivo. */
  valutazione?: Valutazione;
}

export interface OpzioniAllerte {
  /** Data di riferimento, iniettabile: le verifiche non possono dipendere da oggi. */
  oggi?: DataIt;
  /**
   * Obblighi su cui produrre allerte. Omesso significa «tutti».
   *
   * Serve al perimetro del redattore, e non e' cosmesi: un'allerta su un nodo
   * non assegnato e' un comando che l'API rifiuterebbe con un 403, e questo
   * progetto non propone strade che finiscono cosi'. Su un redattore con tre
   * obblighi assegnati l'elenco passa da 119 righe a 3, e l'avviso di
   * avviamento smette di parlare di un albero che non e' suo.
   *
   * L'alberatura completa resta comunque necessaria e va passata per intero:
   * la cadenza di un obbligo si risolve risalendo fino alla sezione, che nel
   * perimetro non c'e'.
   */
  nodiAmmessi?: ReadonlySet<IdNodo>;
}

// --------------------------------------------------------------------------- //
// Chiavi
// --------------------------------------------------------------------------- //

/**
 * Identita' di un'allerta: nodo, tipo, periodo.
 *
 * E' la chiave con cui il verdetto la spegne, quindi **deve restare stabile fra
 * due calcoli sugli stessi dati**. Se cambiasse, i verdetti gia' registrati
 * smetterebbero di combaciare in silenzio, e l'operatore rivedrebbe allerte che
 * credeva chiuse senza capire perche'.
 */
export function chiaveAllerta(
  tipo: TipoAllerta,
  idNodo: IdNodo,
  chiavePeriodo: string,
): string {
  return `${idNodo}|${tipo}|${chiavePeriodo}`;
}

// --------------------------------------------------------------------------- //
// Successione
// --------------------------------------------------------------------------- //

/**
 * La pubblicazione che subentra a un'annualita', se c'e'.
 *
 * Regola, per intero: un'annualita' A ha un successore se **sul medesimo nodo**
 * esiste una pubblicazione in stato `pubblicato` con `anno_riferimento > A`.
 *
 * Una **bozza** per A+1 non e' un successore: non e' pubblica, e l'obbligo resta
 * scoperto agli occhi del cittadino. E' pero' un'evidenza che cambia il
 * giudizio — qualcuno ci sta lavorando — e come tale finisce in riga.
 */
export function successorePerAnnualita(
  pubblicazioni: readonly Pubblicazione[],
  idNodo: IdNodo,
  anno: number,
): Pubblicazione | undefined {
  return pubblicazioni.find(
    (voce) =>
      voce.nodo_id === idNodo &&
      voce.stato === "pubblicato" &&
      voce.anno_riferimento > anno,
  );
}

// --------------------------------------------------------------------------- //
// Stato di avviamento
// --------------------------------------------------------------------------- //

/**
 * Vero se l'ente sta ancora popolando la sezione.
 *
 * Due condizioni, entrambe sufficienti: nessuna pubblicazione, oppure piu' di
 * meta' degli obblighi applicabili senza contenuti. In quello stato un elenco di
 * obblighi vuoti non e' un allarme ed e' sbagliato presentarlo come tale.
 */
export function eEnteInAvvio(
  nodi: readonly Nodo[],
  pubblicazioni: readonly Pubblicazione[],
  nodiAmmessi?: ReadonlySet<IdNodo>,
): boolean {
  const pubblicate = pubblicazioni.filter((voce) => voce.stato === "pubblicato");
  if (pubblicate.length === 0) return true;

  const foglie = nodiFoglia(nodi).filter(
    (nodo) => nodo.applicabile && (!nodiAmmessi || nodiAmmessi.has(nodo.id_nodo)),
  );
  if (foglie.length === 0) return false;
  const conContenuti = new Set(
    pubblicazioni
      .filter((voce) => voce.stato !== "archiviato")
      .map((voce) => voce.nodo_id),
  );
  const scoperte = foglie.filter((nodo) => !conContenuti.has(nodo.id_nodo)).length;
  return scoperte / foglie.length > QUOTA_AVVIO;
}

// --------------------------------------------------------------------------- //
// Calcolo
// --------------------------------------------------------------------------- //

/** Indice dei verdetti attivi, per chiave di allerta. */
function verdettiAttivi(
  valutazioni: readonly Valutazione[],
): ReadonlyMap<string, Valutazione> {
  const mappa = new Map<string, Valutazione>();
  for (const valutazione of valutazioni) {
    // `attiva` la calcola l'API: non revocata e non scaduta. Ricalcolarla qui
    // significherebbe due definizioni della stessa cosa.
    if (!valutazione.attiva) continue;
    if (!valutazione.tipo_allerta || !valutazione.chiave_periodo) continue;
    mappa.set(
      chiaveAllerta(
        valutazione.tipo_allerta,
        valutazione.id_nodo,
        valutazione.chiave_periodo,
      ),
      valutazione,
    );
  }
  return mappa;
}

/** Annualita' presenti su un nodo, dalla piu' recente. */
function annualitaDi(
  pubblicazioni: readonly Pubblicazione[],
  idNodo: IdNodo,
): AnnualitaPresente[] {
  return pubblicazioni
    .filter((voce) => voce.nodo_id === idNodo)
    .map((voce) => ({
      anno: voce.anno_riferimento,
      stato: voce.stato,
      id: voce.id,
      titolo: voce.titolo,
    }))
    .sort((prima, seconda) => seconda.anno - prima.anno);
}

/**
 * Tutte le allerte dell'ente, spente comprese.
 *
 * Le spente restano nell'elenco con il loro verdetto: e' cio' che permette di
 * rileggere una decisione senza andarla a cercare altrove. A nasconderle pensa
 * {@link allerteDaDecidere}, che e' il filtro predefinito della pagina.
 */
export function calcolaAllerte(
  nodi: readonly Nodo[],
  pubblicazioni: readonly Pubblicazione[],
  valutazioni: readonly Valutazione[],
  regole: readonly RegolaObbligo[],
  opzioni: OpzioniAllerte = {},
): Allerta[] {
  const oggi = opzioni.oggi ?? oggiPredefinito();
  const annoCorrente = Number(oggi.slice(6, 10));
  const perIdNodo = indicePerIdNodo(nodi);
  const verdetti = verdettiAttivi(valutazioni);
  // Una regola per nodo. Se ne arrivassero due — non deve accadere, l'unicita'
  // e' sul database — vince la **prima** dell'elenco: e' l'ordine in cui l'API
  // le serve, e una `Map` costruita per assegnazione diretta terrebbe invece
  // l'ultima. Su una ricostruzione ordinata dal piu' recente significava
  // conservare la dichiarazione piu' vecchia, e rendere inefficace ogni
  // correzione.
  const perNodo = new Map<IdNodo, RegolaObbligo>();
  for (const regola of regole) {
    if (!perNodo.has(regola.id_nodo)) perNodo.set(regola.id_nodo, regola);
  }
  const inAvvio = eEnteInAvvio(nodi, pubblicazioni, opzioni.nodiAmmessi);

  const allerte: Allerta[] = [];

  /** Giorni fra oggi e una data, con `oggi` iniettabile. */
  const giorniA = (data: DataIt): number | null => {
    const daOggiVero = giorniDaOggi(data);
    if (opzioni.oggi === undefined || daOggiVero === null) return daOggiVero;
    const scarto = giorniDaOggi(opzioni.oggi);
    return scarto === null ? daOggiVero : daOggiVero - scarto;
  };

  /**
   * `chiaveStorica`: la chiave con cui la stessa allerta si registrava prima.
   * Un verdetto gia' dato con quella resta valido: il giornale non si riscrive,
   * e un'allerta chiusa non deve riaprirsi solo perche' e' cambiato il calcolo.
   */
  const aggiungi = (allerta: Omit<Allerta, "chiave" | "valutazione">, chiaveStorica?: string) => {
    const chiave = chiaveAllerta(allerta.tipo, allerta.id_nodo, allerta.chiave_periodo);
    const valutazione =
      verdetti.get(chiave) ??
      (chiaveStorica
        ? verdetti.get(chiaveAllerta(allerta.tipo, allerta.id_nodo, chiaveStorica))
        : undefined);
    allerte.push({ ...allerta, chiave, valutazione });
  };

  for (const nodo of nodiFoglia(nodi)) {
    if (!nodo.applicabile) continue; // Un nodo non applicabile espone la dicitura e non scade.
    if (opzioni.nodiAmmessi && !opzioni.nodiAmmessi.has(nodo.id_nodo)) continue;

    const regola = perNodo.get(nodo.id_nodo);
    const risolta = cadenzaConFonte(
      nodo,
      perIdNodo,
      regola?.cadenza_dichiarata ?? undefined,
    );
    // Una regola puo' dichiarare la sola cadenza o la sola durata: senza
    // natura, l'obbligo non e' classificato.
    const natura = regola?.natura ?? undefined;
    const annualita = annualitaDi(pubblicazioni, nodo.id_nodo);
    const pubblicate = annualita.filter((voce) => voce.stato === "pubblicato");
    const comune = {
      id_nodo: nodo.id_nodo,
      denominazione_nodo: nodo.denominazione,
      cadenza: risolta.cadenza,
      fonte_cadenza: risolta.fonte,
      id_nodo_cadenza: risolta.id_nodo_fonte,
      natura,
    };

    // ---------------------------------------------------------- scoperto -- //
    if (annualita.filter((voce) => voce.stato !== "archiviato").length === 0) {
      aggiungi({
        ...comune,
        tipo: "obbligo_scoperto",
        chiave_periodo: String(annoCorrente),
        giorni_residui: null, // Non c'e' un termine: non c'e' un numero.
        gravita: inAvvio ? "informativo" : "attenzione",
        motivo:
          "Obbligo applicabile senza alcun contenuto pubblicato: risulta scoperto alle scansioni automatiche della sezione.",
        evidenza: { annualita, documenti: 0, aggiornamento_risalito: false },
        ...(nodo.nota_assenza
          ? {
              nota_assenza: {
                testo: nodo.nota_assenza,
                ...(nodo.nota_assenza_il ? { il: nodo.nota_assenza_il } : {}),
              },
            }
          : {}),
      });
      continue;
    }

    // ------------------------------------- annualita' senza successore -- //
    // Non si apre su un obbligo accumulativo o non ricorrente: li' un'annualita'
    // senza seguito e' il funzionamento normale, non una mancanza. E' il dato
    // che il Comune ha dichiarato, e vale piu' di qualunque deduzione.
    if (
      pubblicate.length > 0 &&
      natura !== "accumulativo" &&
      natura !== "non_ricorrente"
    ) {
      // `pubblicate[0]` e' gia' l'annualita' pubblicata piu' recente, quindi un
      // successore per definizione non c'e': qui c'era una chiamata a
      // `successorePerAnnualita` che non poteva scattare, e faceva sembrare la
      // regola piu' severa di com'e'. Le annualita' precedenti non generano
      // allerta proprio perche' questa le copre tutte.
      const ultima = pubblicate[0];
      const giorniDallInizioAnno = giorniA(`01/01/${ultima.anno + 1}`);
      const scaduta =
        ultima.anno < annoCorrente &&
        giorniDallInizioAnno !== null &&
        -giorniDallInizioAnno > GIORNI_TOLLERANZA_SUCCESSIONE;

      if (scaduta) {
        const bozza = annualita.find(
          (voce) => voce.anno > ultima.anno && voce.stato === "bozza",
        );
        aggiungi({
          ...comune,
          tipo: "annualita_senza_successore",
          chiave_periodo: String(ultima.anno),
          giorni_residui: giorniDallInizioAnno,
          gravita: bozza ? "attenzione" : "critico",
          motivo: bozza
            ? `L'ultimo contenuto pubblicato e' del ${ultima.anno}. Per il ${bozza.anno} esiste una bozza, che non e' ancora pubblica.`
            : `L'ultimo contenuto pubblicato e' del ${ultima.anno}: sull'obbligo non risulta nulla di piu' recente.`,
          evidenza: {
            annualita,
            bozza_successiva: bozza,
            documenti: 0,
            data_ultimo_aggiornamento: nodo.data_ultimo_aggiornamento ?? undefined,
            aggiornamento_risalito: false,
          },
        });
      }
    }

    // --------------------------------------- aggiornamento oltre il termine -- //
    // Non ha senso su un obbligo non ricorrente: non ha una cadenza da violare.
    const aggiornato = nodo.data_ultimo_aggiornamento;
    if (aggiornato && natura !== "non_ricorrente") {
      const trascorsi = giorniA(aggiornato);
      if (trascorsi !== null) {
        const residui = giorniDiCadenza(risolta.cadenza) + trascorsi;
        if (residui < 0) {
          aggiungi({
            ...comune,
            tipo: "aggiornamento_scaduto",
            // La data da cui il termine e' contato: una nuova pubblicazione la
            // cambia, quindi apre un'allerta nuova e un vecchio verdetto non la
            // spegne per sbaglio.
            chiave_periodo: aggiornato.slice(0, 10).split("/").reverse().join(""),
            termine: undefined,
            giorni_residui: residui,
            gravita: "attenzione",
            motivo: `Ultimo aggiornamento ${aggiornato}: oltre il termine della cadenza ${risolta.cadenza}.`,
            evidenza: {
              annualita,
              documenti: 0,
              data_ultimo_aggiornamento: aggiornato,
              // Sulle foglie la data e' del nodo: non c'e' nulla sotto da cui
              // possa essere risalita.
              aggiornamento_risalito: false,
            },
          });
        }
      }
    }
  }

  // ---------------------------------------------- fine pubblicazione -- //
  //
  // Ogni documento ha la propria durata (art. 8 c. 3, oppure quella dichiarata
  // per il documento o per l'obbligo): l'allerta e' per i documenti che finiscono
  // lo stesso giorno nella stessa pubblicazione. Una pubblicazione senza
  // documenti esce tutta intera, con la chiave di prima.
  for (const pubblicazione of pubblicazioni) {
    if (pubblicazione.stato !== "pubblicato") continue;
    const prima = pubblicazione.versioni?.[0]?.data_prima_pubblicazione;
    if (!prima) continue;

    const idNodo = pubblicazione.nodo_id ?? "";
    const nodo = perIdNodo.get(idNodo);
    const regola = perNodo.get(idNodo);
    const risolta = cadenzaConFonte(
      nodo ?? { id_nodo: idNodo, id_padre: null },
      perIdNodo,
      regola?.cadenza_dichiarata ?? undefined,
    );
    const comune = {
      tipo: "quinquennio" as const,
      id_nodo: idNodo,
      denominazione_nodo: nodo?.denominazione ?? idNodo,
      pubblicazione_id: pubblicazione.id,
      cadenza: risolta.cadenza,
      fonte_cadenza: risolta.fonte,
      id_nodo_cadenza: risolta.id_nodo_fonte,
      natura: regola?.natura ?? undefined,
    };
    const evidenza = {
      annualita: annualitaDi(pubblicazioni, idNodo),
      documenti: pubblicazione.documenti?.length ?? 0,
      prima_pubblicazione: prima,
      aggiornamento_risalito: false,
    };

    const documenti = pubblicazione.documenti ?? [];

    // ------------------------------------------------ file da sostituire -- //
    //
    // La scadenza del file e' una data che chi carica dichiara: passata, il file
    // resta online ma va sostituito con uno aggiornato. Non dipende dalla
    // cadenza dell'obbligo, che resta un'allerta a se'. Come per la fine della
    // pubblicazione, i file della stessa pubblicazione che scadono lo stesso
    // giorno stanno in una riga sola: la chiave non puo' usare l'id del
    // documento, che cambia a ogni revisione e riaprirebbe le allerte decise.
    //
    // Quale scadenza conti lo dice `scadenzaDelFile`, la stessa regola di «In
    // arrivo». Un sostituto gia' caricato ma in bozza non chiude l'allerta:
    // online resta il file scaduto, e la riga lo dice in giallo finche' qualcuno
    // non preme «Pubblica». Spegnerla al caricamento era un silenziamento senza
    // data, proprio cio' che il verdetto «in preparazione» non ammette.
    const perValidita = new Map<DataIt, NonNullable<Allerta["documenti"]>>();
    for (const documento of documenti) {
      const validita = scadenzaDelFile(documento);
      if (!validita) continue;
      const residui = giorniA(validita.data);
      if (residui === null || residui >= 0) continue;
      const gruppo = perValidita.get(validita.data) ?? [];
      gruppo.push({
        id: documento.id,
        titolo: documento.titolo,
        ...(validita.inCorrezione ? { in_correzione: true } : {}),
      });
      perValidita.set(validita.data, gruppo);
    }
    for (const [scaduto, gruppo] of perValidita) {
      const daPubblicare = gruppo.filter((documento) => documento.in_correzione).length;
      const tuttiInBozza = daPubblicare === gruppo.length;
      const base =
        gruppo.length === 1
          ? `Il file e' scaduto il ${scaduto}: resta pubblicato, ma va caricata una versione aggiornata.`
          : `${gruppo.length} file sono scaduti il ${scaduto}: restano pubblicati, ma va caricata una versione aggiornata.`;
      aggiungi({
        ...comune,
        tipo: "documento_da_sostituire",
        chiave_periodo: `${pubblicazione.id}-${scaduto.slice(6, 10)}${scaduto.slice(3, 5)}${scaduto.slice(0, 2)}`,
        termine: scaduto,
        documenti: gruppo,
        giorni_residui: giorniA(scaduto),
        gravita: tuttiInBozza ? "attenzione" : "critico",
        motivo: tuttiInBozza
          ? `Il sostituto e' in bozza: online resta il file scaduto il ${scaduto}, finche' non si preme «Pubblica».`
          : daPubblicare > 0
            ? `${base} Per ${daPubblicare === 1 ? "uno" : daPubblicare} il sostituto e' gia' in bozza, da pubblicare.`
            : base,
        evidenza,
      });
    }

    if (documenti.length === 0) {
      const primoFuori =
        pubblicazione.data_archiviazione_prevista ??
        dataArchiviazionePrevista(prima, regola?.durata_anni ?? DURATA_PREDEFINITA_ANNI);
      const termine = primoFuori ? giornoPrima(primoFuori) : null;
      const residui = termine ? giorniA(termine) : null;
      if (!termine || residui === null || residui > GIORNI_ORIZZONTE_QUINQUENNIO) continue;
      aggiungi({
        ...comune,
        chiave_periodo: String(pubblicazione.id),
        termine,
        giorni_residui: residui,
        gravita: residui < 0 ? "attenzione" : "informativo",
        motivo:
          residui < 0
            ? `Pubblicazione terminata il ${termine}: il contenuto deve uscire dalla navigazione corrente, restando accessibile.`
            : `Il contenuto resta in pubblicazione fino al ${termine}.`,
        evidenza,
      });
      continue;
    }

    // Prima di questo calcolo l'allerta era una per pubblicazione, con chiave il
    // solo identificativo e termine i cinque anni dalla prima pubblicazione. Un
    // verdetto dato allora parlava di **quel** giorno: vale per la riga che
    // finisce quel giorno, non per i documenti che finiscono in un altro.
    const primoFuoriStorico = dataArchiviazionePrevista(prima, DURATA_PREDEFINITA_ANNI);
    const termineStorico = primoFuoriStorico ? giornoPrima(primoFuoriStorico) : null;

    // Raggruppati per ultimo giorno: una riga per data, non una per file.
    const perFine = new Map<DataIt, NonNullable<Allerta["documenti"]>>();
    for (const documento of documenti) {
      if (documento.archiviato_il) continue;
      // Un file mai pubblicato non e' online e non scade. Un sostituto in bozza
      // invece si': senza indirizzo ma con la decorrenza ereditata, sta al
      // posto di un documento che online c'e' ancora, e la cui fine non si
      // sposta per una correzione non pubblicata.
      const inCorrezione = !documento.url;
      if (inCorrezione && !documento.pubblicato_dal) continue;
      const fine = fineDelDocumento(documento, regola);
      if (!fine) continue;
      const gruppo = perFine.get(fine) ?? [];
      gruppo.push({
        id: documento.id,
        titolo: documento.titolo,
        ...(inCorrezione ? { in_correzione: true } : {}),
      });
      perFine.set(fine, gruppo);
    }
    for (const [termine, gruppo] of perFine) {
      const residui = giorniA(termine);
      if (residui === null || residui > GIORNI_ORIZZONTE_QUINQUENNIO) continue;
      const quanti = gruppo.length === 1 ? "1 documento" : `${gruppo.length} documenti`;
      const corretti = gruppo.filter((documento) => documento.in_correzione).length;
      const nota =
        corretti === 0
          ? ""
          : corretti === 1
            ? " Uno ha una correzione in bozza: si archivia dopo averla pubblicata o scartata."
            : ` ${corretti} hanno una correzione in bozza: si archiviano dopo averla pubblicata o scartata.`;
      aggiungi(
        {
          ...comune,
          // La data nella chiave: documenti della stessa pubblicazione con
          // durate diverse sono allerte diverse, e un verdetto su una non deve
          // spegnere l'altra.
          chiave_periodo: `${pubblicazione.id}-${termine.slice(6, 10)}${termine.slice(3, 5)}${termine.slice(0, 2)}`,
          termine,
          documenti: gruppo,
          giorni_residui: residui,
          gravita: residui < 0 ? "attenzione" : "informativo",
          motivo:
            (residui < 0
              ? `Pubblicazione terminata il ${termine}: ${quanti} devono uscire dalla navigazione corrente, restando accessibili.`
              : `${quanti} restano in pubblicazione fino al ${termine}.`) + nota,
          evidenza,
        },
        termine === termineStorico ? String(pubblicazione.id) : undefined,
      );
    }
  }

  return allerte.sort(ordinaPerUrgenza);
}

/** Prima cio' che e' piu' in ritardo; le righe senza termine in coda. */
function ordinaPerUrgenza(prima: Allerta, seconda: Allerta): number {
  if (prima.giorni_residui === null && seconda.giorni_residui === null) return 0;
  if (prima.giorni_residui === null) return 1;
  if (seconda.giorni_residui === null) return -1;
  return prima.giorni_residui - seconda.giorni_residui;
}

// --------------------------------------------------------------------------- //
// Viste
// --------------------------------------------------------------------------- //

/**
 * Proposte di natura in attesa di conferma, per nodo.
 *
 * Sono i verdetti che portano una `natura_proposta` e che nessuno ha ancora
 * tradotto in regola. Stanno qui e non nella pagina perche' sono una regola di
 * dominio — chi ha proposto che cosa, e se valga ancora — e perche' senza di
 * esse il percorso «il redattore propone, il Super Admin conferma» si fermava a
 * meta': la proposta veniva scritta e nessuno la vedeva.
 *
 * Si prende la piu' recente, che e' l'ordine in cui l'API le serve, e si
 * scartano i nodi che una regola ce l'hanno gia': quella e' la parola
 * definitiva, e una proposta non la rimette in discussione.
 */
export function proposteDaConfermare(
  valutazioni: readonly Valutazione[],
  regole: readonly RegolaObbligo[],
): ReadonlyMap<IdNodo, Valutazione> {
  // Solo le regole che dichiarano una natura chiudono la proposta: una regola
  // di sola cadenza o di sola durata non dice nulla sulla natura, e la
  // proposta resta da confermare.
  const conRegola = new Set(
    regole.filter((regola) => regola.natura).map((regola) => regola.id_nodo),
  );
  const proposte = new Map<IdNodo, Valutazione>();
  for (const voce of valutazioni) {
    if (!voce.attiva || voce.natura_proposta === null) continue;
    // Un verdetto di portata «obbligo» e' una dichiarazione, non una proposta.
    if (voce.portata !== "periodo") continue;
    if (conRegola.has(voce.id_nodo) || proposte.has(voce.id_nodo)) continue;
    proposte.set(voce.id_nodo, voce);
  }
  return proposte;
}

/** Come e' andata l'archiviazione di un documento, chiesta da «Archivia ora». */
export type EsitoArchiviazione =
  | { titolo: string; stato: "archiviato" }
  | { titolo: string; stato: "non_riuscito"; messaggio: string }
  /** Ha una correzione in bozza: non si e' nemmeno chiesto all'API. */
  | { titolo: string; stato: "rinviato" };

/**
 * L'esito di «Archivia ora» detto per intero: quanti su quanti, e per ciascuno
 * di quelli rimasti il perche'.
 *
 * Un'archiviazione per documento puo' riuscire a meta': dire «Archiviato» dopo
 * il primo rifiuto, o tacere i documenti gia' archiviati quando il terzo fallisce,
 * lascerebbe chi legge a indovinare che cosa e' ancora online.
 */
export function riepilogoArchiviazione(esiti: readonly EsitoArchiviazione[]): {
  esito: string | null;
  errore: string | null;
} {
  const archiviati = esiti.filter((voce) => voce.stato === "archiviato").length;
  const rimasti = esiti
    .filter((voce) => voce.stato !== "archiviato")
    .map((voce) =>
      voce.stato === "rinviato"
        ? `«${voce.titolo}»: ha una correzione in bozza, da pubblicare o scartare prima`
        : `«${voce.titolo}»: ${voce.messaggio}`,
    );
  const esito =
    archiviati === 0
      ? null
      : `Archiviati ${archiviati} di ${esiti.length}: escono dalla navigazione corrente e passano nell'archivio della pagina, restando accessibili ai loro indirizzi.`;
  const errore =
    rimasti.length === 0
      ? null
      : `${rimasti.length === 1 ? "Un documento resta" : `${rimasti.length} documenti restano`} online. ${rimasti.join("; ")}.`;
  return { esito, errore };
}

/** Lunghezza massima della nota pubblica sull'assenza: la stessa dell'API. */
export const NOTA_ASSENZA_MASSIMO = 500;

/**
 * Legge la nota sull'assenza scritta nel modulo. Vuota vuol dire «togliere la
 * nota»: e' un gesto legittimo, non un errore.
 */
export function leggiNotaAssenza(testo: string): { testo: string | null; errore: string | null } {
  const pulito = testo.trim();
  if (!pulito) return { testo: null, errore: null };
  if (pulito.length > NOTA_ASSENZA_MASSIMO) {
    return {
      testo: null,
      errore: `Al massimo ${NOTA_ASSENZA_MASSIMO} caratteri: ora sono ${pulito.length}.`,
    };
  }
  return { testo: pulito, errore: null };
}

/** Le sole allerte su cui nessuno ha ancora deciso. */
export function allerteDaDecidere(allerte: readonly Allerta[]): Allerta[] {
  return allerte.filter((allerta) => allerta.valutazione === undefined);
}

/**
 * Gli **obblighi** in allerta, non le allerte.
 *
 * E' il numero del KPI in testa, ed e' deduplicato di proposito: un nodo con
 * l'annualita' scoperta *e* l'aggiornamento oltre il termine e' un problema, non
 * due, e contarlo due volte gonfierebbe il solo numero che l'operatore guarda
 * prima di decidere da dove cominciare.
 */
export function obblighiInAllerta(allerte: readonly Allerta[]): Set<IdNodo> {
  return new Set(allerte.map((allerta) => allerta.id_nodo));
}

/** Nome di ciascun tipo: intestazione del gruppo in tabella e del suo riquadro numerico. */
export const ETICHETTA_TIPO_ALLERTA: Record<TipoAllerta, string> = {
  documento_da_sostituire: "File da sostituire",
  annualita_senza_successore: "Annualita' senza successore",
  aggiornamento_scaduto: "Aggiornamento scaduto",
  quinquennio: "Fine pubblicazione",
  obbligo_scoperto: "Obblighi senza contenuti",
};

// --------------------------------------------------------------------------- //
// La pagina «Scadenze e allerte»: filtri, conteggi, conferma della natura
// --------------------------------------------------------------------------- //
//
// Stanno qui e non nella pagina perche' sono regole, e le verifiche devono
// poterle raggiungere: che cosa conta un riquadro, che cosa mostra la tabella
// quando lo si preme, quando un'allerta e' «da gestire» e quando «in arrivo».

/**
 * Vero se la natura dell'obbligo ha qualcosa da dire su un tipo di allerta.
 *
 * Sul file da sostituire non ne ha: la scadenza l'ha scelta chi ha caricato il
 * file, e nessuna natura la spegne. Offrire li' «vale per sempre», o «Conferma
 * la natura», scriveva una regola dell'obbligo — che spegneva altre allerte —
 * lasciando aperta proprio quella da cui si era partiti.
 */
export function naturaPertinente(tipo: TipoAllerta): boolean {
  return tipo !== "documento_da_sostituire";
}

/** Vero se chi guarda puo' confermare, da questa riga, la natura proposta da un redattore. */
export function puoConfermareNatura(
  allerta: Pick<Allerta, "tipo" | "id_nodo">,
  eSuperAdmin: boolean,
  proposte: ReadonlyMap<IdNodo, Valutazione>,
): boolean {
  return eSuperAdmin && naturaPertinente(allerta.tipo) && proposte.has(allerta.id_nodo);
}

/** Il filtro dei riquadri numerici: un tipo di allerta, oppure «in arrivo». */
export type FiltroAllerte = TipoAllerta | "in_arrivo";

/** Il filtro letto dall'indirizzo (`?tipo=`); un valore sconosciuto vale «nessun filtro». */
export function filtroDaParametro(valore: string | null): FiltroAllerte | null {
  if (valore === "in_arrivo") return valore;
  return (TIPI_ALLERTA as readonly string[]).includes(valore ?? "") ? (valore as TipoAllerta) : null;
}

/**
 * Dove porta un indirizzo della vecchia pagina delle allerte.
 *
 * `?scoperti=1` era il rimando del cruscotto agli obblighi senza contenuti, e
 * deve atterrare sullo stesso filtro: senza, su un ente in avviamento si
 * arrivava a una tabella vuota.
 */
export function destinazioneDaAllerte(parametri: URLSearchParams): string {
  return parametri.get("scoperti") === "1"
    ? "/scadenzario?tipo=obbligo_scoperto"
    : "/scadenzario";
}

/**
 * Le allerte della scheda «Da gestire»: tutte, tranne le fini di pubblicazione
 * che devono ancora arrivare. Il calcolo le produce fino a un anno prima, ma
 * le stesse date le mostra gia' «In arrivo», e contarle due volte faceva
 * leggere come da gestire un documento che scade fra sei mesi.
 */
export function allerteDaGestire(allerte: readonly Allerta[]): Allerta[] {
  return allerte.filter(
    (allerta) =>
      !(allerta.tipo === "quinquennio" && allerta.giorni_residui !== null && allerta.giorni_residui >= 0),
  );
}

/** Vero se una scadenza fra `giorni` giorni sta in «In arrivo» con l'orizzonte scelto. */
export function eInArrivo(giorni: number, orizzonte: number): boolean {
  return giorni >= 0 && giorni <= orizzonte;
}

/** Chi guarda, per le regole che dipendono dal ruolo. */
export interface ContestoVista {
  eSuperAdmin: boolean;
  /** Proposte di natura in attesa di conferma ({@link proposteDaConfermare}). */
  propostePerNodo: ReadonlyMap<IdNodo, Valutazione>;
}

/**
 * Vero se un'allerta chiede ancora qualcosa a chi guarda: nessuno ha deciso,
 * oppure — per il Super Admin — c'e' una natura proposta da confermare.
 *
 * E' la definizione di «Solo da decidere» **e** di cio' che contano i
 * riquadri: con una sola definizione il numero coincide con le righe che il
 * riquadro apre.
 */
export function daDecidere(allerta: Allerta, contesto: ContestoVista): boolean {
  return (
    allerta.valutazione === undefined ||
    puoConfermareNatura(allerta, contesto.eSuperAdmin, contesto.propostePerNodo)
  );
}

/**
 * Le righe di «Da gestire» con i filtri della pagina.
 *
 * Su un ente in avviamento gli obblighi scoperti sono un piano di lavoro, non
 * allerte: restano fuori finche' non si preme il loro riquadro.
 */
export function allerteVisibili(
  daGestire: readonly Allerta[],
  opzioni: ContestoVista & {
    soloDaDecidere: boolean;
    filtro: FiltroAllerte | null;
    inAvvio: boolean;
  },
): Allerta[] {
  let elenco = opzioni.soloDaDecidere
    ? daGestire.filter((allerta) => daDecidere(allerta, opzioni))
    : [...daGestire];
  if (opzioni.filtro && opzioni.filtro !== "in_arrivo") {
    elenco = elenco.filter((allerta) => allerta.tipo === opzioni.filtro);
  } else if (opzioni.inAvvio) {
    elenco = elenco.filter((allerta) => allerta.tipo !== "obbligo_scoperto");
  }
  return elenco;
}

/**
 * Quante allerte da decidere per tipo: i numeri in testa a «Scadenze e
 * allerte», che sono anche i filtri della tabella.
 *
 * Si contano le allerte e non gli obblighi, con la stessa definizione di
 * {@link daDecidere}: con «Solo da decidere» ogni numero e' la lunghezza del
 * gruppo che il suo riquadro apre; con «Anche le decise» il gruppo e' piu'
 * lungo. Senza `contesto` conta le sole allerte senza verdetto.
 */
export function conteggiPerTipo(
  allerte: readonly Allerta[],
  contesto: ContestoVista = { eSuperAdmin: false, propostePerNodo: new Map() },
): Record<TipoAllerta, number> {
  const conteggi = Object.fromEntries(TIPI_ALLERTA.map((tipo) => [tipo, 0])) as Record<
    TipoAllerta,
    number
  >;
  for (const allerta of allerte) {
    if (daDecidere(allerta, contesto)) conteggi[allerta.tipo] += 1;
  }
  return conteggi;
}

/**
 * Priorita' di ciascun tipo nella pagina, dal piu' urgente.
 *
 * Un `Record` e non un elenco: se domani si aggiunge un tipo di allerta, il
 * compilatore chiede la sua priorita'. Un elenco scritto a mano lo lasciava
 * fuori in silenzio, e le sue righe sparivano da tabella e riquadri.
 */
const PRIORITA_TIPO: Record<TipoAllerta, number> = {
  documento_da_sostituire: 0,
  annualita_senza_successore: 1,
  aggiornamento_scaduto: 2,
  quinquennio: 3,
  obbligo_scoperto: 4,
};

/**
 * L'ordine in cui la pagina mostra i tipi, nei riquadri e nei gruppi della
 * tabella: prima cio' che e' piu' urgente.
 */
export const ORDINE_TIPI_ALLERTA: readonly TipoAllerta[] = [...TIPI_ALLERTA].sort(
  (primo, secondo) => PRIORITA_TIPO[primo] - PRIORITA_TIPO[secondo],
);

/** Allerte raggruppate per sorgente, nell'ordine in cui la pagina le mostra. */
export function raggruppaPerTipo(
  allerte: readonly Allerta[],
): { tipo: TipoAllerta; allerte: Allerta[] }[] {
  return ORDINE_TIPI_ALLERTA
    .map((tipo) => ({
      tipo,
      allerte: allerte.filter((allerta) => allerta.tipo === tipo),
    }))
    .filter((gruppo) => gruppo.allerte.length > 0);
}
